import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowUUpLeft } from "@phosphor-icons/react";

/**
 * "Undo this import" button rendered inline on the wizard's result screen.
 * The global `UndoImportBanner` still handles cross-navigation, but this
 * control is front-and-center on the page where the mistake happened.
 *
 * Swaps to a green "Import undone" pill once the API returns success, and
 * dispatches `csv-import-complete` so the global banner re-polls.
 */
export default function UndoInline({ batchId, onUndone }) {
  const [busy, setBusy] = useState(false);
  const [undone, setUndone] = useState(false);

  if (undone) {
    return (
      <span className="inline-flex items-center gap-2 border border-emerald-700 bg-emerald-50 text-emerald-800 px-2 py-1 text-[11px] font-bold uppercase tracking-widest">
        Import undone
      </span>
    );
  }

  const clickUndo = async () => {
    const ok = window.confirm(
      "Undo this import? Deletes the leads + timeline. Refused if any lead has already been delivered externally.",
    );
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/csv-import/batches/${batchId}/undo`);
      toast.success(`Undone — ${data.deleted} leads removed`);
      setUndone(true);
      window.dispatchEvent(new CustomEvent("csv-import-complete"));
      onUndone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Undo failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={clickUndo}
      disabled={busy}
      data-testid="csv-result-undo"
      className="border-2 border-black bg-yellow-300 px-3 py-2 text-xs uppercase tracking-widest font-bold hover:bg-black hover:text-white disabled:opacity-60 inline-flex items-center gap-2"
    >
      <ArrowUUpLeft size={12} weight="bold" /> {busy ? "Undoing…" : "Undo this import"}
    </button>
  );
}
